import { storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { isOriginInUse, isStockInUse, parseSelectFilterOptions } from '../../util/search';
import { parse, stringify } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { filterCompletedListings } from '../../util/listingsHelpers';
import { listingMatchesCondition } from '../../util/listingCondition';
import { listingMatchesDeliveryOptions } from '../../util/listingDeliveryOptions';
import {
  isWarehouseChannel,
  isWarehouseListing,
  WAREHOUSE_SOURCE_QUERY_VALUE,
} from '../../util/itemSourceChannel';
import { filterListingsByBrand, getAvailableBrandValues } from '../../util/brandAvailability';
import { fetchCreators, fetchStoresWithReviews } from '../../ducks/user.duck';
import { isNotEmptyString } from '../../util/string';
import { mapTaxonomySearchParamsToSdkQuery, validFilterParams } from './SearchPage.shared';

const { LatLng, LatLngBounds } = sdkTypes;

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
// const RESULT_PAGE_SIZE = 24;
const RESULT_PAGE_SIZE = 20; // Increased to compensate for manufacturer listings that will be filtered out

const defaultLoadOptions = {
  shouldFetchFullResults: true,
  useClientSidePagination: false,
  stockFilter: null,
};

export const resolveStockFilters = (datesMaybe, enforcedStockFilter = null) => {
  const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

  if (enforcedStockFilter) {
    return enforcedStockFilter;
  }

  return hasDatesFilterInUse ? {} : { minStock: 0, stockMode: 'match-undefined' };
};

const normalizeLoadOptions = loadOptionsOrShouldFetchFullResults => {
  if (
    typeof loadOptionsOrShouldFetchFullResults === 'object' &&
    loadOptionsOrShouldFetchFullResults
  ) {
    return {
      ...defaultLoadOptions,
      ...loadOptionsOrShouldFetchFullResults,
    };
  }

  return {
    ...defaultLoadOptions,
    shouldFetchFullResults:
      typeof loadOptionsOrShouldFetchFullResults === 'boolean'
        ? loadOptionsOrShouldFetchFullResults
        : defaultLoadOptions.shouldFetchFullResults,
  };
};

const filterListingsByDeliveryOption = (listings, deliveryFilterRaw) => {
  if (!deliveryFilterRaw) {
    return listings;
  }

  const selectedOptions = parseSelectFilterOptions(String(deliveryFilterRaw));
  if (!selectedOptions.length) {
    return listings;
  }

  return (listings || []).filter(listing => {
    const publicData = listing?.attributes?.publicData || {};
    return listingMatchesDeliveryOptions(publicData, selectedOptions);
  });
};

const filterListingsByCondition = (listings, conditionFilterRaw) => {
  if (!conditionFilterRaw) {
    return listings;
  }

  const selectedConditions = parseSelectFilterOptions(String(conditionFilterRaw));
  if (!selectedConditions.length) {
    return listings;
  }

  return (listings || []).filter(listing => {
    const publicData = listing?.attributes?.publicData || {};
    return listingMatchesCondition(publicData, selectedConditions);
  });
};

const filterListingsByChannel = (listings, channelRaw) => {
  const isWarehouse = isWarehouseChannel(channelRaw);
  return (listings || []).filter(listing => {
    const listingIsWarehouse = isWarehouseListing(listing);
    return isWarehouse ? listingIsWarehouse : !listingIsWarehouse;
  });
};

const filterListingsByApprovedSeller = (listings, included = []) => {
  const includedUsers = included.filter(item => item.type === 'user') || [];

  return (listings || []).filter(listing => {
    const authorId = listing.relationships?.author?.data?.id?.uuid;
    const author = includedUsers.find(user => user.id.uuid === authorId);
    return author?.attributes?.profile?.publicData?.approved_seller === true;
  });
};

const applyPostQueryListingFilters = (response, params, channel) => {
  response.data.data = filterCompletedListings(response?.data?.data || []);
  response.data.data = filterListingsByCondition(response?.data?.data || [], params?.pub_condition);
  response.data.data = filterListingsByDeliveryOption(
    response?.data?.data || [],
    params?.pub_deliveryOption
  );
  response.data.data = filterListingsByChannel(response?.data?.data || [], channel);
  response.data.data = filterListingsByApprovedSeller(
    response?.data?.data || [],
    response?.data?.included || []
  );

  return response;
};

const applyBrandFilterToResponse = (response, brandFilter) => {
  response.data.data = filterListingsByBrand(response?.data?.data || [], brandFilter);
  return response;
};

const fetchAllListingPages = (sdk, queryParams, firstResponse) => {
  const totalPages = firstResponse?.data?.meta?.totalPages || 1;

  if (totalPages <= 1) {
    return Promise.resolve(firstResponse);
  }

  const firstPage = Number(queryParams?.page) || 1;
  const remainingPages = [];

  for (let page = 1; page <= totalPages; page++) {
    if (page !== firstPage) {
      remainingPages.push(
        sdk.listings.query({ ...queryParams, page }).then(response => ({ page, response }))
      );
    }
  }

  return Promise.all(remainingPages).then(restResponses => {
    const responsesByPage = new Map([[firstPage, firstResponse]]);

    restResponses.forEach(({ page, response }) => {
      responsesByPage.set(page, response);
    });

    let allData = [];
    let allIncluded = [];

    for (let page = 1; page <= totalPages; page++) {
      const pageResponse = responsesByPage.get(page);
      if (!pageResponse?.data) {
        continue;
      }

      allData = allData.concat(pageResponse.data.data || []);
      allIncluded = allIncluded.concat(pageResponse.data.included || []);
    }

    firstResponse.data.data = allData;
    firstResponse.data.included = allIncluded;

    return firstResponse;
  });
};

const paginateResponseLocally = (response, requestedPage, perPage) => {
  const allData = response?.data?.data || [];
  const totalItems = allData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
  const safePage = Math.min(Math.max(Number(requestedPage) || 1, 1), totalPages);
  const startIndex = (safePage - 1) * perPage;

  response.data.data = allData.slice(startIndex, startIndex + perPage);
  response.data.meta = {
    ...(response?.data?.meta || {}),
    page: safePage,
    perPage,
    totalItems,
    totalPages,
  };

  return response;
};

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const ALL_LISTINGS_SUCCESS = 'app/SearchPage/ALL_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  mapListingsIds: [],
  availableBrandValues: null,
};

const resultIds = data => data.data.map(l => l.id);
const areSearchParamsEqual = (a, b) => stringify(a || {}) === stringify(b || {});

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      if (!areSearchParamsEqual(state.searchParams, payload.searchParams)) {
        return state;
      }
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        availableBrandValues: payload.availableBrandValues || null,
        searchInProgress: false,
      };
    case ALL_LISTINGS_SUCCESS:
      if (!areSearchParamsEqual(state.searchParams, payload.searchParams)) {
        return state;
      }
      return {
        ...state,
        mapListingsIds: resultIds(payload.data),
        pagination: payload.data.meta,
        availableBrandValues: payload.availableBrandValues || state.availableBrandValues,
      };
    case SEARCH_LISTINGS_ERROR:
      if (!areSearchParamsEqual(state.searchParams, payload.searchParams)) {
        return state;
      }
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = (response, searchParams, availableBrandValues = null) => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data, searchParams, availableBrandValues },
});

export const allListingsSuccess = (response, searchParams, availableBrandValues = null) => ({
  type: ALL_LISTINGS_SUCCESS,
  payload: { data: response.data, searchParams, availableBrandValues },
});

export const searchListingsError = (e, searchParams) => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: { ...e, searchParams },
});

export const searchListings = (
  searchParams,
  config,
  isSearch = true,
  prevResponse = null,
  loadOptionsOrShouldFetchFullResults = defaultLoadOptions
) => async (dispatch, getState, sdk) => {
  let state = getState();
  let { domLoaded = false } = state?.ui || {};
  let requestedSearchParams = { ...(searchParams || {}) };
  const loadOptions = normalizeLoadOptions(loadOptionsOrShouldFetchFullResults);
  const { shouldFetchFullResults, useClientSidePagination, stockFilter } = loadOptions;
  if (!isSearch && !prevResponse) {
    let sdkResponse = await sdk.currentUser.show();
    let listingsId =
      sdkResponse?.data?.data?.attributes?.profile?.privateData?.recentlyViewedListings || [];
    requestedSearchParams.ids = listingsId.join(',');
  }
  if (!prevResponse) {
    dispatch(searchListingsRequest(requestedSearchParams));
  }

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const getNextPagination = (res, perPage) => {
    let totalItems = res?.data?.meta?.totalItems || perPage;
    return totalItems < 100 ? totalItems : 100;
  };

  const {
    perPage,
    price,
    dates,
    sort,
    channel,
    pub_brand: brandFilter,
    ...rest
  } = requestedSearchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = resolveStockFilters(datesMaybe, stockFilter);
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };
  const channelMaybe = isWarehouseChannel(channel)
    ? { pub_source: WAREHOUSE_SOURCE_QUERY_VALUE }
    : {};

  const cleanQueryParams = {
    ...rest,
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...sortMaybe,
    ...channelMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    pub_isFromManufacturer: false,
    perPage: prevResponse ? getNextPagination(prevResponse, perPage) : perPage,
  };

  const mappedQueryParams = mapTaxonomySearchParamsToSdkQuery(cleanQueryParams);
  const params = {
    ...mappedQueryParams,
    'fields.listing': [...(mappedQueryParams['fields.listing'] || [])],
  };

  params['fields.listing'].push('description');
  params['fields.listing'].push('publicData.brand');
  params['fields.listing'].push('publicData.isFromManufacturer');
  // params['fields.listing'].push('metadata');

  return sdk.listings
    .query(params)
    .then(async response => {
      let availableBrandValues = null;

      if (useClientSidePagination && !prevResponse) {
        response = await fetchAllListingPages(sdk, params, response);
      }

      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      response = applyPostQueryListingFilters(response, params, channel);
      availableBrandValues = getAvailableBrandValues(response?.data?.data || []);
      response = applyBrandFilterToResponse(response, brandFilter);

      if (useClientSidePagination && !prevResponse) {
        response = paginateResponseLocally(
          response,
          requestedSearchParams.page,
          requestedSearchParams.perPage || RESULT_PAGE_SIZE
        );
      }

      if (
        !useClientSidePagination &&
        (params?.pub_deliveryOption || params?.pub_condition || channel || brandFilter) &&
        response?.data?.meta
      ) {
        response.data.meta.totalItems = response.data.data.length;
      }

      const creatorsId = (response?.data?.data || [])
        .map(l => l?.attributes?.publicData?.creatorId)
        .filter(l => l);
      if (domLoaded) {
        dispatch(fetchCreators(creatorsId));
        dispatch(fetchStoresWithReviews());
      }
      dispatch(addMarketplaceEntities(response, sanitizeConfig));

      if (!prevResponse) {
        //First call
        dispatch(searchListingsSuccess(response, requestedSearchParams, availableBrandValues));

        //Second call
        if (isSearch && shouldFetchFullResults && !useClientSidePagination) {
          return dispatch(
            searchListings(requestedSearchParams, config, isSearch, response, loadOptions)
          );
        }
      } else {
        //Second call
        prevResponse.data.meta.totalItems = (response.data.data || []).length;
        response.data.meta = prevResponse.data.meta;
        dispatch(allListingsSuccess(response, requestedSearchParams, availableBrandValues));
      }

      return response;
    })
    .catch(e => {
      if (prevResponse) {
        // The follow-up query is only used to expand the full result set
        // (e.g. map data). Keep the already loaded primary results instead
        // of surfacing a fatal search error for visible listings.
        console.error('SearchPage secondary query failed, keeping primary results:', e);
        dispatch(allListingsSuccess(prevResponse, requestedSearchParams));
        return prevResponse;
      }

      dispatch(searchListingsError(storableError(e), requestedSearchParams));
      throw e;
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadRecentlyViewedData = (params, search, config) => {
  return processData({}, config, false);
};

export const loadData = (params, search, config, shouldFetchFullResults = true) => {
  const loadOptions = normalizeLoadOptions(shouldFetchFullResults);
  let queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  if (params?.category && isNotEmptyString(params.category)) {
    queryParams.pub_category = params.category;
  }

  const listingFieldsConfig = config?.listing?.listingFields || [];
  const defaultFiltersConfig = config?.search?.defaultFilters || [];
  queryParams = validFilterParams(queryParams, listingFieldsConfig, defaultFiltersConfig, false);

  return processData(queryParams, config, true, loadOptions);
};

export const processData = (
  queryParams,
  config,
  isSearch = true,
  loadOptionsOrShouldFetchFullResults = defaultLoadOptions
) => {
  const loadOptions = normalizeLoadOptions(loadOptionsOrShouldFetchFullResults);
  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  let bodyParams = {
    ...rest,
    ...originMaybe,
    page,
    perPage: RESULT_PAGE_SIZE,
    sort: rest?.sort ? rest.sort + ',pub_rank' : 'createdAt,pub_rank',
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'publicData.listingType',
      'publicData.condition',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      // These help rendering of 'purchase' listings,
      // when transitioning from search page to listing page
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.deliveryOption',

      'publicData.isAssociated',
      'publicData.creatorId',
      'publicData.creatorType',
      'publicData.deliveryPreference',
      'publicData.shippingAddress',
      'publicData.location',
      'publicData.storedAddress',
      'publicData.category',
      'publicData.subcategory',
      'publicData.videos',
      'publicData.isManufacturer',
      'publicData.source',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.profileImage',
      'profile.publicData',
    ],
    'fields.image': [
      // Only request the essential variants for search listings
      'variants.landscape-crop', // Primary display
      `variants.${variantPrefix}`, // Card variant
      'variants.square-small', // Fallback
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    'limit.images': 1,
  };

  return searchListings(bodyParams, config, isSearch, null, loadOptions);
};
